import DefaultButton from 'components/button/DefaultButton';
import DefaultLayout from 'components/layout/DefaultLayout';
import TableToolbar from 'components/table/TableToolbar';
import React, { useEffect, useState } from 'react';
import MainTable from 'components/table/MainTable';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import styled from 'styled-components';
import { theme } from 'utils/style/theme';
import SearchInput from 'components/search/SearchInput';
import { assetsApis } from 'apis/assets/assetsApis';
import { Image, Spin, message } from 'antd';
import NoImage from 'components/etc/NoImage';
import { Edit2, Trash } from 'iconsax-react';
import ActionButton from 'components/table/ActionButton';
import listIcon from 'utils/icon/icon_list-black.png';
import caregoryIcon from 'utils/icon/icon_category-black.png';
import GridList from './GridList';
import { LoadingOutlined } from '@ant-design/icons';
import { useCookies } from 'react-cookie';
import CreateModal from './Modal/CreateModal';
import { handleMessage } from 'utils/helper/message';
import DeleteModal from 'components/modal/DeleteModal';
import UpdateModal from './Modal/UpdateModal';
import { UserRole } from '../../utils/types/common';
/**
 * * 패키지 관리 - 목록
 * - 등록된 패키지가 특정 작품을 연결하는 과정 수행
 */

export interface IVisible {
  create: boolean;
  uploading: boolean;
  edit: boolean;
  delete: boolean;
}

const AssetsPage = () => {
  const { t } = useTranslation();
  const [cookie] = useCookies();
  const [visible, setVisible] = useState<IVisible>({
    create: false,
    uploading: false,
    edit: false,
    delete: false,
  });
  const [type, setType] = useState<string>('bg');
  const [searchData, setSearchData] = useState<string>('');
  const [assetsData, setAssetsData] = useState<any>(null);
  const [tableData, setTableData] = useState<any>(null);
  const [selectedData, setSelectedData] = useState<any>(null);
  const [displayType, setDisplayType] = useState<string>('list');
  const [refresh, setRefresh] = useState<boolean>(false);
  const [loading, setLoading] = useState<boolean>(false);
  const antLoadingIcon = <LoadingOutlined style={{ fontSize: 50 }} spin />;

  const handleFetchData = async () => {
    try {
      setLoading(true);
      const res = await assetsApis.assetsList(type);
      setAssetsData(res.list);
      setLoading(false);
    } catch (err) {
      setLoading(false);
      console.log('fetch data error: ', err);
    }
  };

  useEffect(() => {
    if (assetsData) {
      setTableData(assetsData);
    }
  }, [assetsData]);

  useEffect(() => {
    handleFetchData();
  }, [refresh, type]);

  // handle search input
  useEffect(() => {
    if (searchData && searchData.length) {
      const cloneData = JSON.parse(JSON.stringify(assetsData));
      const filterData = cloneData.filter((e) =>
        e.image_name.toLowerCase().includes(searchData.toLowerCase()),
      );
      setTableData(filterData);
    } else {
      setTableData(assetsData);
    }
  }, [searchData, refresh, type]);

  const handleSwichTab = (type) => {
    setType(type);
  };

  const handleClickDeleteModal = (record) => {
    setSelectedData(record);
    setVisible({
      ...visible,
      delete: true,
    });
  };
  const handleDelete = async () => {
    if (selectedData) {
      try {
        setLoading(true);

        await assetsApis.assetsDelete(type, selectedData.id);
        setRefresh(!refresh);
        setLoading(false);
        setVisible({
          ...visible,
          delete: false,
        });
      } catch (err) {
        setLoading(false);
        handleMessage(message, 'error', 'Please try again');
        console.log('Delete error:', err);
      }
    }
  };

  const tableActionMenu = (record) => {
    return [
      {
        key: 1,
        item: (
          <Edit2
            size='24'
            onClick={() => {
              setSelectedData(record);
              setVisible({ ...visible, edit: true });
            }}
          />
        ),
        hoverColor: theme.colors.blue_500,
        color: theme.colors.dark_200,
      },

      {
        key: 2,
        item: (
          <Trash
            size='24'
            onClick={() => {
              handleClickDeleteModal(record);
            }}
          />
        ),
        hoverColor: theme.colors.red_500,
        color: theme.colors.dark_200,
      },
    ];
  };

  const columnsOrigin = [
    {
      title: 'ID',
      dataIndex: 'id',
      width: 'fit-content',
      align: 'center',
    },
    {
      title: t('assets.table.review'),
      dataIndex: 'image_url',
      width: '10rem',
      align: 'center',
      render: (_, record) => (
        <IDColumnWrapper>
          {_ === null || _ === '' ? (
            <NoImage />
          ) : (
            <Image
              src={_}
              alt={record.image_name}
              height={80}
              width={94}
              style={{ objectFit: 'cover' }}
            />
          )}
        </IDColumnWrapper>
      ),
    },
    {
      title: t('assets.table.name'),
      dataIndex: 'image_name',
      width: '20rem',
    },
    {
      dataIndex: '',
      width: '8rem',
      render: (text, record) => {
        if (cookie?.loginAdmin?.role === UserRole.SuperAdmin) {
          const menus = [tableActionMenu(record)];
          return <ActionButton menus={menus} />;
        } else {
          return <></>;
        }
      },
    },
  ];

  return (
    <>
      <DefaultLayout>
        <TabWrapper>
          <div
            className={`${type === 'bg' ? 'active' : 'not-active'}`}
            onClick={() => handleSwichTab('bg')}
          >
            <p>{t('assets.tabs.background')}</p>
          </div>
          <div
            className={`${type === 'minicut' ? 'active' : 'not-active'}`}
            onClick={() => handleSwichTab('minicut')}
          >
            <p>{t('assets.tabs.minicut')}</p>
          </div>
        </TabWrapper>
        <TableToolbar>
          <SearchInput placeholder='Search...' setSearchData={setSearchData} />
          <DefaultButton
            onClick={() => setVisible({ ...visible, create: true })}
          >
            {type === 'bg'
              ? t('assets.header.buttons.createBg')
              : t('assets.header.buttons.createMini')}
          </DefaultButton>
        </TableToolbar>
        <TableListWrapper>
          <div
            className={`${displayType === 'list' ? 'category' : 'uncategory'}`}
            onClick={() => setDisplayType('list')}
          >
            <img src={listIcon} alt='list icon' />
          </div>
          <div
            className={`${
              displayType === 'category' ? 'category' : 'uncategory'
            }`}
            onClick={() => setDisplayType('category')}
          >
            <img src={caregoryIcon} alt='list icon' />
          </div>
        </TableListWrapper>
        {loading ? (
          <LoadingWrapper>
            <Spin indicator={antLoadingIcon} />
          </LoadingWrapper>
        ) : displayType === 'category' ? (
          <GridList
            data={tableData}
            visible={visible}
            setSelectedData={setSelectedData}
            selectedData={selectedData}
            setVisible={setVisible}
            handleClickDeleteModal={handleClickDeleteModal}
          />
        ) : (
          <MainTable columns={columnsOrigin} dataSource={tableData} />
        )}
      </DefaultLayout>
      <CreateModal
        visible={visible}
        setVisible={setVisible}
        refresh={refresh}
        setRefresh={setRefresh}
        type={type}
      />
      <DeleteModal
        visible={visible.delete}
        setVisible={(e) => {
          setVisible({ ...visible, delete: e });
        }}
        title={
          type === 'bg'
            ? t('assets.modal.background.delete.title')
            : t('assets.modal.minicut.delete.title')
        }
        content={
          type === 'bg'
            ? t('assets.modal.background.delete.content')
            : t('assets.modal.minicut.delete.content')
        }
        label={selectedData?.image_name}
        handleDelete={handleDelete}
        loading={loading}
      />
      <UpdateModal
        visible={visible}
        setVisible={setVisible}
        refresh={refresh}
        setRefresh={setRefresh}
        type={type}
        selectedData={selectedData}
        setSelectedData={setSelectedData}
      />
    </>
  );
};

export default AssetsPage;

const TabWrapper = styled.div`
  margin-bottom: 24px;
  padding: 8px;
  background-color: ${theme.colors.blue_50};
  display: flex;
  border-radius: 9px;
  .not-active,
  .active {
    width: 50%;
    cursor: pointer;
  }
  .not-active p,
  .active p {
    color: ${theme.colors.dark_500};
    font-size: ${theme.fontSizes.md};
    padding: 10px 0;
    text-align: center;
  }
  .not-active p {
    font-weight: ${theme.fontWeights.normal};
  }
  .active p {
    font-weight: ${theme.fontWeights.bold};
    border-radius: 7px;
    border: 1px solid rgba(0, 0, 0, 0.04);
    background: #ffffff;
    box-shadow: 0px 3px 1px 0px rgba(0, 0, 0, 0.04),
      0px 3px 8px 0px rgba(0, 0, 0, 0.12);
  }
`;

const IDColumnWrapper = styled.div`
  display: flex;
  column-gap: 56px;
  display: flex;
  align-items: center;
  justify-content: center;
`;

const TableListWrapper = styled.div`
  display: flex;
  justify-content: end;
  align-items: center;
  margin-bottom: 28px;
  margin-top: 5px;
  border-bottom: 1px solid ${theme.colors.dark_100};
  padding: 24px 0;
  img {
    cursor: pointer;
  }
  .category {
    padding: 10px;
    background-color: ${theme.colors.dark_50};
    border-radius: 6px;
  }
  .uncategory {
    padding: 10px;
  }
  & > * + * {
    margin-left: 8px;
  }
`;

const LoadingWrapper = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
`;
